import React, { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Eraser } from "lucide-react";
import { timeAgo } from "./ReplicaBanner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");
const TONE = { pending: "text-amber-200", approved: "text-sky-200", completed: "text-emerald-200", denied: "text-rose-200", expired: "text-white/40", cancelled: "text-white/40" };

/** Hub: reset-to-blank requests from registered environments — approve / deny. */
export function ResetApprovalsPanel({ resets, canWrite, onChanged }) {
    const [busy, setBusy] = useState(null);
    const decide = async (r, decision) => {
        const verb = decision === "approve" ? "APPROVE" : "deny";
        const note = window.prompt(`${verb} the reset of ${r.env_name} requested by ${r.requester_email}?${decision === "approve" ? " The environment erases its entire database within 30 s." : ""}\n\nOptional note (audited):`, "");
        if (note === null) return;
        setBusy(r.request_id);
        try {
            await api.post(`/admin/sync/resets/${r.request_id}/${decision}`, { note: note || null });
            toast.success(decision === "approve" ? "Reset approved" : "Reset denied");
            onChanged?.();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(null);
        }
    };
    const pending = (resets || []).filter((r) => r.status === "pending");
    const past = (resets || []).filter((r) => r.status !== "pending");
    return (
        <div className="bg-[#0a0a0a] border border-white/10 rounded-md p-6" data-testid="reset-approvals-panel">
            <div className="flex items-center gap-2 mb-1">
                <Eraser className="w-4 h-4 text-[#7C3AED]" strokeWidth={1.5} />
                <div className="font-display text-lg tracking-tight">Reset approvals</div>
                {pending.length > 0 && <span className="ml-2 rounded bg-amber-400/10 border border-amber-400/30 px-2 py-0.5 text-[11px] text-amber-100" data-testid="reset-pending-count">{pending.length} pending</span>}
            </div>
            <p className="text-xs text-white/50 mb-4">An environment's super admin requested and e-mail-confirmed a reset to blank. Approval here is the third and final signal; the environment then drops its whole database and reopens <code>/init</code>. Requests expire after 24 h.</p>
            <ul className="space-y-2" data-testid="reset-pending-list">
                {pending.map((r) => (
                    <li key={r.request_id} className="rounded border border-amber-400/30 bg-amber-400/5 p-3 text-xs flex flex-wrap items-center gap-3" data-testid={`reset-row-${r.request_id}`}>
                        <div>
                            <div className="text-white/90">{r.env_name} <span className="text-white/40">· {r.channel}</span></div>
                            <div className="text-white/60">requested by {r.requester_email} · {timeAgo(r.requested_at)} · confirmed {timeAgo(r.confirmed_at)} · expires {fmt(r.expires_at)}</div>
                            <div className="font-mono text-white/30">{r.request_id}</div>
                        </div>
                        {canWrite && (
                            <div className="ml-auto flex items-center gap-2">
                                <button disabled={busy === r.request_id} onClick={() => decide(r, "deny")} className="rounded border border-white/10 px-3 py-1.5 text-white/70 hover:bg-white/5 disabled:opacity-40" data-testid={`reset-deny-${r.request_id}`}>Deny</button>
                                <button disabled={busy === r.request_id} onClick={() => decide(r, "approve")} className="rounded bg-rose-500/80 hover:bg-rose-500 px-3 py-1.5 text-white disabled:opacity-40" data-testid={`reset-approve-${r.request_id}`}>Approve reset</button>
                            </div>
                        )}
                    </li>
                ))}
                {!pending.length && <li className="text-xs text-white/40" data-testid="reset-none">No pending requests.</li>}
            </ul>
            {!!past.length && (
                <div className="mt-4">
                    <div className="label-xs mb-2">Trail</div>
                    <ul className="text-xs space-y-1" data-testid="reset-trail">
                        {past.slice(0, 8).map((r) => (
                            <li key={r.request_id} className="flex flex-wrap items-center gap-2 border-t border-white/5 py-1.5" data-testid={`reset-trail-${r.request_id}`}>
                                <span className={`font-medium ${TONE[r.status] || ""}`}>{r.status}</span>
                                <span className="text-white/80">{r.env_name}</span>
                                <span className="text-white/50">{r.requester_email}</span>
                                <span className="ml-auto text-white/40">{r.decided_by ? `${r.decided_by} · ` : ""}{fmt(r.completed_at || r.decided_at || r.updated_at)}</span>
                                {r.note && <span className="w-full text-white/40 italic">{r.note}</span>}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
